/**
 * TranslationService - Core translation engine.
 * Framework-agnostic service that handles Google Translate API calls,
 * caching, chunking, and language detection.
 */
import axios from 'axios';

import type {
	TranslationServiceConfig,
	TranslationRequest,
	TranslationResult,
	TranslationStats,
	TranslationProgressCallback,
} from './types';
import { TranslationError } from './types';

/**
 * Default configuration values.
 */
const DEFAULTS = {
	CHUNK_SIZE: 120,
	TARGET_LANGUAGE: 'en',
	ENABLE_DETECTION: false,
} as const;

/**
 * Get browser's preferred language code.
 */
function getBrowserLanguage(): string {
	if (typeof navigator !== 'undefined') {
		const lang = navigator.language || (navigator as any).userLanguage;
		return lang?.split('-')[0] || DEFAULTS.TARGET_LANGUAGE;
	}
	return DEFAULTS.TARGET_LANGUAGE;
}

/**
 * Decode HTML entities from translated text.
 */
function decodeHtmlEntities(str: string): string {
	if (typeof document !== 'undefined') {
		const txt = document.createElement('textarea');
		txt.innerHTML = str;
		return txt.value;
	}
	// Fallback for non-browser environments
	return str
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'");
}

/**
 * TranslationService class.
 * Singleton pattern recommended - create once and reuse across the application.
 */
export class TranslationService {
	private cache = new Map<string, string>();
	private config: Required<TranslationServiceConfig>;
	private stats: TranslationStats = {
		totalCharacters: 0,
		cacheHits: 0,
		apiCalls: 0,
		lastTranslationUsedApi: false,
	};

	constructor(config: TranslationServiceConfig) {
		this.config = {
			apiKey: config.apiKey,
			targetLanguage: config.targetLanguage || getBrowserLanguage(),
			chunkSize: config.chunkSize || DEFAULTS.CHUNK_SIZE,
			enableDetection: config.enableDetection ?? DEFAULTS.ENABLE_DETECTION,
		};
	}

	/**
	 * Generate cache key for a text and language pair.
	 */
	private cacheKey(text: string): string {
		return `${text}__${this.config.targetLanguage}`;
	}

	/**
	 * Check if a text is already cached.
	 */
	isCached(text: string): boolean {
		return this.cache.has(this.cacheKey(text));
	}

	/**
	 * Get cached translation if available.
	 */
	getCached(text: string): string | undefined {
		return this.cache.get(this.cacheKey(text));
	}

	/**
	 * Get current translation statistics.
	 */
	getStats(): TranslationStats {
		return { ...this.stats };
	}

	/**
	 * Reset translation statistics.
	 */
	resetStats(): void {
		this.stats = {
			totalCharacters: 0,
			cacheHits: 0,
			apiCalls: 0,
			lastTranslationUsedApi: false,
		};
	}

	/**
	 * Clear the translation cache.
	 * Useful for memory management or forcing re-translation.
	 */
	clearCache(): void {
		this.cache.clear();
	}

	/**
	 * Get current target language.
	 */
	getTargetLanguage(): string {
		return this.config.targetLanguage;
	}

	/**
	 * Update target language.
	 * Note: Cached translations for different languages are preserved.
	 */
	setTargetLanguage(language: string): void {
		this.config.targetLanguage = language;
	}

	/**
	 * Translate multiple texts in optimized batches.
	 * Automatically handles caching, chunking, and parallel requests.
	 *
	 * @param requests - Array of translation requests
	 * @param onProgress - Optional callback for progress updates
	 * @returns Array of translation results
	 */
	async translate(
		requests: TranslationRequest[],
		onProgress?: TranslationProgressCallback,
	): Promise<TranslationResult[]> {
		if (requests.length === 0) {
			return [];
		}

		// Separate cached from uncached requests
		const uncachedRequests: TranslationRequest[] = [];
		const cachedResults: TranslationResult[] = [];

		for (const request of requests) {
			const cached = this.getCached(request.text);
			if (cached !== undefined) {
				cachedResults.push({
					key: request.key,
					original: request.text,
					translated: cached,
				});
				this.stats.cacheHits++;
			} else {
				uncachedRequests.push(request);
			}
		}

		// If all cached, return immediately
		if (uncachedRequests.length === 0) {
			this.stats.lastTranslationUsedApi = false;
			onProgress?.({ completed: requests.length, total: requests.length, percentage: 100 });
			return this.buildFinalResults(requests, cachedResults);
		}

		// Translate uncached texts via API
		const apiResults = await this.translateViaApi(uncachedRequests, onProgress);
		this.stats.lastTranslationUsedApi = true;

		// Combine cached and API results
		return this.buildFinalResults(requests, [...cachedResults, ...apiResults]);
	}

	/**
	 * Translate a simple key-value object.
	 * Convenience method for simpler use cases.
	 *
	 * @param texts - Object with keys and text values
	 * @returns Object with same keys and translated values
	 */
	async translateObject<T extends Record<string, string>>(texts: T): Promise<T> {
		const requests: TranslationRequest[] = Object.entries(texts).map(([key, text]) => ({
			key,
			text,
		}));

		const results = await this.translate(requests);

		const translated = {} as T;
		for (const result of results) {
			(translated as Record<string, string>)[result.key] = result.translated;
		}

		// Preserve any keys not in results (shouldn't happen, but safety)
		for (const key of Object.keys(texts)) {
			if (!(key in translated)) {
				(translated as Record<string, string>)[key] = texts[key];
			}
		}

		return translated;
	}

	/**
	 * Internal: Translate via Google Translate API with chunking.
	 */
	private async translateViaApi(
		requests: TranslationRequest[],
		onProgress?: TranslationProgressCallback,
	): Promise<TranslationResult[]> {
		const { apiKey, targetLanguage, chunkSize, enableDetection } = this.config;
		const results: TranslationResult[] = [];
		let completedCount = 0;

		// Optional: Detect languages to skip already-translated text
		let skipIndices = new Set<number>();
		if (enableDetection && requests.length > 0) {
			skipIndices = await this.detectAndSkip(requests, targetLanguage);
		}

		// Filter requests that need translation
		const toTranslate = requests.filter((_, i) => !skipIndices.has(i));

		// Handle skipped requests (already in target language)
		for (const index of skipIndices) {
			const request = requests[index];
			// Cache as-is (no translation needed)
			this.cache.set(this.cacheKey(request.text), request.text);
			results.push({
				key: request.key,
				original: request.text,
				translated: request.text,
			});
			completedCount++;
		}

		if (toTranslate.length === 0) {
			onProgress?.({ completed: requests.length, total: requests.length, percentage: 100 });
			return results;
		}

		// Split into chunks and translate in parallel
		const chunks: TranslationRequest[][] = [];
		for (let i = 0; i < toTranslate.length; i += chunkSize) {
			chunks.push(toTranslate.slice(i, i + chunkSize));
		}

		const chunkPromises = chunks.map(async (chunk) => {
			try {
				const response = await axios.post(
					`https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
					{
						q: chunk.map((r) => r.text),
						target: targetLanguage,
					},
				);

				const translations = response.data.data.translations;
				const chunkResults: TranslationResult[] = [];

				for (let i = 0; i < chunk.length; i++) {
					const request = chunk[i];
					const translated = decodeHtmlEntities(translations[i].translatedText);

					// Cache the translation
					this.cache.set(this.cacheKey(request.text), translated);

					// Track character count
					this.stats.totalCharacters += request.text.length;

					chunkResults.push({
						key: request.key,
						original: request.text,
						translated,
					});
				}

				completedCount += chunk.length;
				onProgress?.({
					completed: completedCount,
					total: requests.length,
					percentage: Math.round((completedCount / requests.length) * 100),
				});

				return chunkResults;
			} catch (error: any) {
				// Handle rate limiting
				if (error?.response?.status === 429) {
					throw new TranslationError(
						'Translation API rate limit exceeded',
						'RATE_LIMIT',
						error,
					);
				}
				// Handle other API errors
				if (error?.response) {
					throw new TranslationError(
						`Translation API error: ${error.response.statusText}`,
						'API_ERROR',
						error,
					);
				}
				// Handle network errors
				throw new TranslationError('Network error during translation', 'NETWORK_ERROR', error);
			}
		});

		this.stats.apiCalls += chunks.length;

		try {
			const chunkResults = await Promise.all(chunkPromises);
			results.push(...chunkResults.flat());
		} catch (error) {
			// On error, return what we have (cached + any completed chunks)
			// Rethrow the error after logging
			console.error('Translation API error:', error);
			throw error;
		}

		return results;
	}

	/**
	 * Internal: Detect languages and return indices to skip (already in target language).
	 */
	private async detectAndSkip(
		requests: TranslationRequest[],
		targetLanguage: string,
	): Promise<Set<number>> {
		const skipIndices = new Set<number>();

		try {
			const response = await axios.post(
				`https://translation.googleapis.com/language/translate/v2/detect?key=${this.config.apiKey}`,
				{ q: requests.map((r) => r.text) },
			);

			const detections = response.data.data.detections;
			detections.forEach((detection: any[], index: number) => {
				const detectedLang = detection[0]?.language;
				if (detectedLang === targetLanguage) {
					skipIndices.add(index);
				}
			});
		} catch (error) {
			// On detection error, just translate everything
			console.warn('Language detection failed, translating all texts:', error);
		}

		return skipIndices;
	}

	/**
	 * Internal: Build final results array maintaining original request order.
	 */
	private buildFinalResults(
		originalRequests: TranslationRequest[],
		results: TranslationResult[],
	): TranslationResult[] {
		const resultMap = new Map(results.map((r) => [r.key, r]));

		return originalRequests.map((request) => {
			const result = resultMap.get(request.key);
			if (result) {
				return result;
			}
			// Fallback: return original text if somehow missing
			return {
				key: request.key,
				original: request.text,
				translated: request.text,
			};
		});
	}
}
